import { Box, Columns, Container, Heading } from '@spaceship-fspl/components';
import {
  Article,
  ArticleContentContainer,
  getContentPageLayoutStaticProps,
  getMetadataPropsFromPrismicSlice,
  mapNodeToHeaderProps,
  Metadata,
  MetadataProps,
  withContentPageLayout,
} from 'components';
import {
  constructUIDByParamsList,
  getHeader,
  getPathsBasedOnUIDs,
  getPrivacyPolicy,
  getPrivacyUIDs,
  mapSlicesToProps,
  RichText,
} from 'data/prismic';
import {
  Privacy_PolicyBodyPage,
  Privacy_PolicyBodyPagePrimary,
} from 'data/prismic/types';
import { GetStaticPaths, NextPage } from 'next';
import { ParsedUrlQuery } from 'querystring';

interface PrivacyPolicyProps {
  data: {
    Privacy_policyBodyMetadata?: MetadataProps | null;
    Privacy_policyBodyPage?: Privacy_PolicyBodyPagePrimary | null;
  };
}

const PARAM_NAME = 'privacy';
interface PrivacyPolicyContext extends ParsedUrlQuery {
  [PARAM_NAME]: Array<string>;
}

export const getStaticPaths: GetStaticPaths = async () => {
  const uids = await getPrivacyUIDs();

  return {
    paths: getPathsBasedOnUIDs(uids, PARAM_NAME),
    fallback: false,
  };
};

export const getStaticProps = getContentPageLayoutStaticProps<
  PrivacyPolicyProps,
  PrivacyPolicyContext
>(async (context) => {
  const privacyPolicyUID = constructUIDByParamsList(
    context?.params?.[PARAM_NAME],
  );
  const header = await getHeader();
  const document = await getPrivacyPolicy(privacyPolicyUID);
  const data = mapSlicesToProps(document, {
    Privacy_policyBodyMetadata: (slice) =>
      getMetadataPropsFromPrismicSlice(slice),
    Privacy_policyBodyPage: (slice: Privacy_PolicyBodyPage) => ({
      ...slice.primary,
    }),
  });
  return {
    props: {
      layout: {
        headerProps: mapNodeToHeaderProps(header),
      },
      data,
    },
  };
});

const PrivacyPolicy: NextPage<PrivacyPolicyProps> = ({
  data: { Privacy_policyBodyMetadata: metadata, Privacy_policyBodyPage: page },
}) => (
  <>
    {metadata && <Metadata {...metadata} />}

    {page && (
      <Box
        marginTop={{ xs: 'lg', lg: 'xl' }}
        marginBottom={{ xs: 'xl', md: 'xxl', lg: 'xxxl' }}
      >
        <Container>
          <Columns alignX="center" spaceY={{ xs: 'lg', lg: 'xl' }}>
            <Columns.Column width={{ xs: 1, lg: 10 / 12 }}>
              <Heading variant={1} align="center">
                {page.title}
              </Heading>
            </Columns.Column>

            <Columns.Column width={{ xs: 1, md: 6 / 8, lg: 7 / 12 }}>
              <ArticleContentContainer>
                <Article size="sm">
                  <RichText render={page.content} />
                </Article>
              </ArticleContentContainer>
            </Columns.Column>
          </Columns>
        </Container>
      </Box>
    )}
  </>
);

export default withContentPageLayout()(PrivacyPolicy);
